// src/components/ResetPasswordPage.jsx
// Rendered when the user clicks the reset link from their email:
//   /reset-password?token=<raw_token>
// App.jsx pulls the token from the URL and passes it as a prop.

import { useState, useEffect } from 'react';
import { api } from '../utils/api.js';

const PWD_RULES = [
  { id: 'len',   label: 'At least 8 characters',        test: p => p.length >= 8 },
  { id: 'upper', label: 'At least one uppercase letter', test: p => /[A-Z]/.test(p) },
  { id: 'num',   label: 'At least one number',           test: p => /[0-9]/.test(p) },
];

const IconEye = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const IconEyeOff = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);
const IconAlert = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);
const IconCheck = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);

function PwdInput({ label, value, onChange, autoFocus }) {
  const [focused,  setFocused]  = useState(false);
  const [showPwd,  setShowPwd]  = useState(false);

  return (
    <div style={{ marginBottom: 20 }}>
      <label style={{
        display: 'block', marginBottom: 8,
        fontSize: 12, fontWeight: 600, letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: focused ? 'var(--c-gold)' : 'var(--c-muted)',
        fontFamily: 'var(--f-mono)', transition: 'color 0.2s',
      }}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          type={showPwd ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          autoFocus={autoFocus}
          placeholder="••••••••"
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '12px 46px 12px 16px',
            background: focused ? 'rgba(196,160,90,0.04)' : 'rgba(255,255,255,0.025)',
            border: '1px solid',
            borderColor: focused ? 'rgba(196,160,90,0.6)' : 'rgba(255,255,255,0.08)',
            borderRadius: 8, color: 'var(--c-text)', fontSize: 14,
            fontFamily: 'var(--f-body)', outline: 'none',
            transition: 'border-color 0.2s, background 0.2s, box-shadow 0.2s',
            boxShadow: focused ? '0 0 0 3px rgba(196,160,90,0.07)' : 'none',
          }}
        />
        <button
          type="button"
          onClick={() => setShowPwd(v => !v)}
          style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: showPwd ? 'var(--c-gold)' : 'rgba(255,255,255,0.25)', display: 'flex', padding: 0 }}
        >
          {showPwd ? <IconEyeOff /> : <IconEye />}
        </button>
      </div>
    </div>
  );
}

export default function ResetPasswordPage({ token, onDone }) {
  const [pwd,       setPwd]     = useState('');
  const [confirm,   setConfirm] = useState('');
  const [error,     setError]   = useState('');
  const [loading,   setLoading] = useState(false);
  const [submitted, setSubmit]  = useState(false);
  const [done,      setDone]    = useState(false);
  const [countdown, setCountdown] = useState(5);

  // Auto-redirect to sign in 5 seconds after successful reset
  useEffect(() => {
    if (!done) return;
    const interval = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          clearInterval(interval);
          onDone();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [done]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    setError('');
    setSubmit(true);

    if (!pwd)                   return setError('Please enter a new password.');
    if (pwd.length < 8)         return setError('Password must be at least 8 characters.');
    if (!/[A-Z]/.test(pwd))     return setError('Password needs at least one uppercase letter.');
    if (!/[0-9]/.test(pwd))     return setError('Password needs at least one number.');
    if (pwd !== confirm)        return setError('Passwords do not match.');

    setLoading(true);
    try {
      await api('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password: pwd }),
      });
      setDone(true);
    } catch (e) {
      setError(e.message || 'Something went wrong. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const onEnter = e => { if (e.key === 'Enter') handleSubmit(); };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Mono:wght@300;400;500&display=swap');
        :root {
          --c-bg: #0b0d11; --c-surface: #111318;
          --c-border: rgba(255,255,255,0.07); --c-gold: #c4a05a;
          --c-gold-lt: #e0c080; --c-text: #e8e4dc;
          --c-muted: rgba(232,228,220,0.4); --c-green: #2dd4a0;
          --f-body: 'Sora', sans-serif; --f-mono: 'DM Mono', monospace;
          --t: 0.2s cubic-bezier(0.4,0,0.2,1);
        }
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        input::placeholder { color: rgba(232,228,220,0.2); font-size: 13.5px; }
        input:-webkit-autofill, input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0 1000px #111318 inset !important;
          -webkit-text-fill-color: #e8e4dc !important;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        .rp-btn {
          width: 100%; padding: 14px;
          background: linear-gradient(135deg, var(--c-gold) 0%, #a0803a 100%);
          border: none; border-radius: 10px; color: #0b0d11;
          font-size: 13px; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; font-family: var(--f-mono);
          cursor: pointer; transition: all var(--t);
          box-shadow: 0 4px 24px rgba(196,160,90,0.2);
          display: flex; align-items: center; justify-content: center; gap: 10px;
        }
        .rp-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 36px rgba(196,160,90,0.35); }
        .rp-btn:disabled { background: rgba(196,160,90,0.15); color: rgba(11,13,17,0.4); cursor: default; box-shadow: none; }
        .rp-link { color: var(--c-gold); cursor: pointer; font-weight: 600; border-bottom: 1px solid rgba(196,160,90,0.3); padding-bottom: 1px; transition: color var(--t); }
        .rp-link:hover { color: var(--c-gold-lt); }
      `}</style>

      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--c-bg)', fontFamily: 'var(--f-body)', color: 'var(--c-text)',
        padding: '24px 16px',
      }}>
        {/* Background glow */}
        <div style={{ position: 'fixed', top: '40%', left: '50%', transform: 'translate(-50%,-50%)', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(196,160,90,0.04) 0%, transparent 65%)', pointerEvents: 'none' }} />

        <div style={{ width: '100%', maxWidth: 420, animation: 'fadeUp 0.4s ease both', position: 'relative', zIndex: 1 }}>

          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <img
              src="/kfxlogo.png"
              alt="KFX Journal"
              style={{ width: 72, height: 72, margin: '0 auto 4px', display: 'block', objectFit: 'contain', borderRadius: 16 }}
            />
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--c-text)', marginBottom: 5 }}>KFX Journal</div>
            <div style={{ fontSize: 13, color: 'var(--c-muted)', fontFamily: 'var(--f-mono)', letterSpacing: '0.03em' }}>
              {done ? 'Password updated' : 'Set a new password'}
            </div>
          </div>

          {/* Card */}
          <div style={{ background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 14, padding: '28px 28px 24px', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}>

            {/* ── Success state ── */}
            {done ? (
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(45,212,160,0.08)', border: '1px solid rgba(45,212,160,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: 'var(--c-green)' }}>
                  <IconCheck />
                </div>
                <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--c-text)', marginBottom: 10 }}>Password changed!</p>
                <p style={{ fontSize: 13, color: 'var(--c-muted)', lineHeight: 1.75, marginBottom: 24 }}>
                  Your password has been updated. You can now sign in with your new credentials.
                </p>
                <button className="rp-btn" onClick={onDone}>
                  Go to Sign In <IconArrow />
                </button>
                <p style={{ marginTop: 14, fontSize: 12, color: 'rgba(232,228,220,0.25)', fontFamily: 'var(--f-mono)' }}>
                  Redirecting automatically in <strong style={{ color: 'var(--c-gold)' }}>{countdown}s</strong>…
                </p>
              </div>
            ) : (
              /* ── Form state ── */
              <div onKeyDown={onEnter}>
                <p style={{ fontSize: 13.5, color: 'var(--c-muted)', lineHeight: 1.7, marginBottom: 22 }}>
                  Choose a strong password. It must be at least 8 characters and include an uppercase letter and a number.
                </p>

                {/* Error banner */}
                {error && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 14px', marginBottom: 20, background: 'rgba(245,101,101,0.07)', border: '1px solid rgba(245,101,101,0.2)', borderRadius: 8, color: '#fca5a5', fontSize: 13, lineHeight: 1.55 }}>
                    <IconAlert /><span>{error}</span>
                  </div>
                )}

                <PwdInput label="New Password"     value={pwd}     onChange={e => setPwd(e.target.value)}     autoFocus />

                {/* Inline rules */}
                {(pwd || submitted) && (
                  <div style={{ marginTop: -10, marginBottom: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {PWD_RULES.map(r => {
                      const ok = r.test(pwd);
                      return (
                        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontFamily: 'var(--f-body)', color: ok ? '#2dd4a0' : (submitted && !ok ? '#f87171' : 'rgba(232,228,220,0.45)'), transition: 'color 0.2s' }}>
                          <span style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, background: ok ? 'rgba(45,212,160,0.15)' : (submitted && !ok ? 'rgba(248,113,113,0.15)' : 'rgba(255,255,255,0.06)'), border: `1px solid ${ok ? 'rgba(45,212,160,0.4)' : (submitted && !ok ? 'rgba(248,113,113,0.35)' : 'rgba(255,255,255,0.1)')}`, transition: 'all 0.2s' }}>
                            {ok ? '✓' : '✗'}
                          </span>
                          {r.label}
                        </div>
                      );
                    })}
                  </div>
                )}

                <PwdInput label="Confirm Password" value={confirm} onChange={e => setConfirm(e.target.value)} />

                <button className="rp-btn" onClick={handleSubmit} disabled={loading} style={{ marginTop: 8 }}>
                  {loading
                    ? <><span style={{ width: 14, height: 14, border: '2px solid rgba(11,13,17,0.2)', borderTopColor: 'rgba(11,13,17,0.7)', borderRadius: '50%', animation: 'spin 0.6s linear infinite', display: 'inline-block' }} /> Saving…</>
                    : <>Set New Password <IconArrow /></>
                  }
                </button>

                <p style={{ textAlign: 'center', marginTop: 18, fontSize: 13, color: 'var(--c-muted)', fontFamily: 'var(--f-body)' }}>
                  Remember it?{' '}
                  <span className="rp-link" onClick={onDone}>Sign in instead</span>
                </p>
              </div>
            )}
          </div>

          <p style={{ textAlign: 'center', marginTop: 18, fontSize: 11, color: 'rgba(232,228,220,0.2)', fontFamily: 'var(--f-mono)', letterSpacing: '0.04em' }}>
            KFX Journal · Kigali Forex University
          </p>
        </div>
      </div>
    </>
  );
}